import { NgClass, NgForOf, NgIf } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import {
  FormBuilder,
  ReactiveFormsModule,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { FederalEntity } from '@core/models/FederalEntity';
import { GeneroVisible } from '@core/models/GeneroVisible';
import { RegistroAsentamiento } from '@core/models/Registration';
import { RegistrationService } from '@core/http/registration.service';
import { PrivacyPoliciesConsentComponent } from '@shared/components/privacy-policies-consent/privacy-policies-consent.component';
import { FileUploadComponent } from '@shared/components/file-upload/file-upload.component';
import { REGEX } from '@shared/utils/regex.constants';
import { debounceTime, distinctUntilChanged } from 'rxjs';
import { NgxMatIntlTelInputComponent } from 'ngx-mat-intl-tel-input';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-public-patient-registration',
  standalone: true,
  imports: [
    NgIf,
    NgClass,
    NgForOf,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatButtonModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    FileUploadComponent,
    PrivacyPoliciesConsentComponent,
    NgxMatIntlTelInputComponent,
  ],
  templateUrl: './public-patient-registration.component.html',
  styleUrl: './public-patient-registration.component.scss',
})
export class PublicPatientRegistrationComponent implements OnInit {
  readonly ID_TIPO_IDENTIFICACION_SIN_ASIGNAR = 0;
  readonly ID_TIPO_IDENTIFICACION_PASAPORTE = 1;
  readonly ID_TIPO_IDENTIFICACION_DOCUMENTO = 2;
  readonly ID_TIPO_IDENTIFICACION_REPRESENTANTE = 3;
  readonly fallbackImage = 'assets/images/paciente.png';

  @ViewChild(PrivacyPoliciesConsentComponent)
  private policiesConsent?: PrivacyPoliciesConsentComponent;

  userForm: UntypedFormGroup;
  patientForm: UntypedFormGroup;
  assetsForm: UntypedFormGroup;

  settlements: RegistroAsentamiento[] = [];
  federalEntity: FederalEntity[] = [];
  generosVisibles: GeneroVisible[] = [];
  preferredCountries: string[] = ['mx', 'es', 'us'];
  submitting = false;
  createdPatientId: string | null = null;

  constructor(
    private fb: FormBuilder,
    private registrationService: RegistrationService,
  ) {
    this.userForm = this.fb.group({
      usr: ['', [Validators.required, Validators.pattern(REGEX.user)]],
      email: ['', [Validators.required, Validators.pattern(REGEX.correo)]],
      nombres: ['', [Validators.required, Validators.pattern(REGEX.text)]],
      primerApellido: ['', [Validators.required, Validators.pattern(REGEX.text)]],
      segundoApellido: ['', [Validators.pattern(REGEX.text)]],
      settlementSearch: ['', [Validators.required]],
      domicilio: ['', [Validators.required, Validators.pattern(REGEX.address)]],
      movil: [null, [Validators.required]],
    });

    this.patientForm = this.fb.group({
      fechaNacimiento: [null, [Validators.required]],
      idEntidadNacimiento: [null, [Validators.required]],
      genero: [null],
      idGeneroVisible: [null, [Validators.required]],
      idTipoIdentificacion: [this.ID_TIPO_IDENTIFICACION_SIN_ASIGNAR, [Validators.required]],
      numeroIdentificacion: [{ value: 'N/A', disabled: true }, [Validators.required]],
    });

    this.assetsForm = this.fb.group({
      imagen: [null],
      firma: [null],
      politicasPrivacidad: [[], [Validators.required]],
    });
  }

  ngOnInit(): void {
    this.registrationService.getFederalEntities().subscribe({
      next: (response) => {
        this.federalEntity = [...(response.data ?? [])];
      },
    });

    this.registrationService.getVisibleGenders().subscribe({
      next: (response) => {
        this.generosVisibles = [...(response.data ?? [])].filter((item) => item.activo);
      },
    });

    this.userForm.get('settlementSearch')?.valueChanges
      .pipe(debounceTime(350), distinctUntilChanged())
      .subscribe((value: unknown) => {
        if (typeof value === 'string' && REGEX.postCode.test(value)) {
          this.searchSettlements(value);
          return;
        }

        if (typeof value !== 'object') {
          this.settlements = [];
        }
      });
  }

  get resolvedPanelImage(): string {
    const raw = this.assetsForm.get('imagen')?.value;

    if (!raw?.dataUrl) {
      return this.fallbackImage;
    }

    return raw.dataUrl;
  }

  displaySettlement(settlement: RegistroAsentamiento | null): string {
    if (!settlement) return '';
    return `${settlement.asentamiento ?? ''}`;
  }

  setSelectedSettlement(value: RegistroAsentamiento): void {
    this.userForm.patchValue({ settlementSearch: value }, { emitEvent: false });
  }

  onSettlementBlur(): void {
    const current = this.userForm.get('settlementSearch')?.value;
    if (!current || typeof current !== 'object' || !current.idAsentamiento) {
      this.userForm.patchValue({ settlementSearch: null }, { emitEvent: false });
    }
  }

  async submit(): Promise<void> {
    this.createdPatientId = null;
    this.userForm.markAllAsTouched();
    this.patientForm.markAllAsTouched();

    if (this.userForm.invalid || this.patientForm.invalid) {
      return;
    }

    const consentAccepted = await this.policiesConsent?.ensureAccepted();
    if (!consentAccepted) {
      return;
    }

    const settlement = this.userForm.get('settlementSearch')?.value as RegistroAsentamiento | null;
    if (!settlement?.idAsentamiento) {
      Swal.fire('COLONIA REQUERIDA', 'SELECCIONA UNA COLONIA VALIDA DE LA LISTA.', 'warning').then();
      return;
    }

    const user = this.userForm.getRawValue();
    const patient = this.patientForm.getRawValue();

    const payload = {
      usuario: {
        usr: user.usr,
        email: user.email,
        nombres: user.nombres,
        primerApellido: user.primerApellido,
        segundoApellido: user.segundoApellido || null,
        idAsentamiento: settlement.idAsentamiento,
        domicilio: user.domicilio,
        movil: this.toPhoneValue(user.movil),
      },
      paciente: {
        fechaNacimiento: this.toDateOnly(patient.fechaNacimiento),
        idEntidadNacimiento: patient.idEntidadNacimiento,
        genero: this.getGeneroPayload(patient.idGeneroVisible),
        idGeneroVisible: patient.idGeneroVisible ?? null,
        idTipoIdentificacion: patient.idTipoIdentificacion,
        numeroIdentificacion: this.getNumeroIdentificacionPayload(),
        alergias: [],
        molecules: [],
        patologias: [],
        idTutor: null,
      },
      politicasPrivacidad: this.getAcceptedPolicies(),
      imagen: this.toAssetPayload(this.assetsForm.get('imagen')?.value),
    };

    this.submitting = true;
    this.registrationService.registerPatient(payload).subscribe({
      next: (response) => {
        this.submitting = false;
        this.createdPatientId = response.data ?? null;

        Swal.fire({
          icon: 'success',
          title: 'REGISTRO COMPLETADO',
          text: this.createdPatientId
            ? `ID DE PACIENTE GENERADO: ${this.createdPatientId}`
            : (response.message || 'EL PACIENTE FUE REGISTRADO.'),
        }).then();

        this.userForm.reset();
        this.patientForm.reset({
          genero: null,
          idGeneroVisible: null,
          idTipoIdentificacion: this.ID_TIPO_IDENTIFICACION_SIN_ASIGNAR,
          numeroIdentificacion: 'N/A',
        });
        this.patientForm.get('numeroIdentificacion')?.disable({ emitEvent: false });
        this.assetsForm.reset({ politicasPrivacidad: [] });
        this.settlements = [];
      },
      error: () => {
        this.submitting = false;
      },
    });
  }

  onTipoIdentificacionChange(): void {
    const tipo = Number(this.patientForm.get('idTipoIdentificacion')?.value ?? 0);
    const numeroCtrl = this.patientForm.get('numeroIdentificacion');
    if (!numeroCtrl) return;

    if (tipo === this.ID_TIPO_IDENTIFICACION_SIN_ASIGNAR) {
      numeroCtrl.setValue('N/A', { emitEvent: false });
      numeroCtrl.disable({ emitEvent: false });
      return;
    }

    numeroCtrl.enable({ emitEvent: false });
    if ((numeroCtrl.value ?? '').toString().trim().toUpperCase() === 'N/A') {
      numeroCtrl.setValue('', { emitEvent: false });
    }
  }

  get numeroIdentificacionReadonly(): boolean {
    return Number(this.patientForm.get('idTipoIdentificacion')?.value ?? 0) === this.ID_TIPO_IDENTIFICACION_SIN_ASIGNAR;
  }

  get documentoIdentidadLabel(): string {
    return 'DNI';
  }

  get documentoRepresentanteLabel(): string {
    return 'DNI DEL REPRESENTANTE';
  }

  private searchSettlements(codigoPostal: string): void {
    this.registrationService.getSettlementsByPostalCode(codigoPostal).subscribe({
      next: (response) => {
        this.settlements = [...(response.data ?? [])];
      },
      error: () => {
        this.settlements = [];
      },
    });
  }

  private toPhoneValue(raw: any): string | null {
    if (!raw) return null;
    if (typeof raw === 'string') return raw.trim() || null;
    return raw.e164Number || raw.e164 || raw.number || null;
  }

  private toDateOnly(value: any): string | null {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;

    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  private toAssetPayload(raw: any) {
    if (!raw?.base64) return null;

    return {
      blobBase64: raw.base64,
      contentType: raw.contentType,
      bytesSize: raw.bytesSize,
      widthPx: raw.widthPx ?? null,
      heightPx: raw.heightPx ?? null,
      hashSha256Base64: raw.hashSha256Base64 ?? null,
    };
  }

  private getNumeroIdentificacionPayload(): string {
    const numeroCtrl = this.patientForm.get('numeroIdentificacion');
    return Number(this.patientForm.get('idTipoIdentificacion')?.value ?? 0) === this.ID_TIPO_IDENTIFICACION_SIN_ASIGNAR
      ? 'N/A'
      : String(numeroCtrl?.value ?? '').trim();
  }

  private getAcceptedPolicies() {
    return (this.assetsForm.get('politicasPrivacidad')?.value ?? [])
      .filter((item: any) => item?.aceptado)
      .map((item: any) => ({
        idPoliticaPrivacidad: Number(item.idPoliticaPrivacidad),
        aceptado: true,
      }));
  }

  private getGeneroPayload(idGeneroVisible: number | null): string | null {
    if (idGeneroVisible == null) {
      return null;
    }

    const generoVisible = this.generosVisibles.find((item) => item.id === Number(idGeneroVisible));
    return generoVisible?.descripcion?.trim() || null;
  }
}
